import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';

import { ListingFooterViewModel } from 'models';

import { AnalyticsConfig, classNames } from 'uitk-react-utils';
import { UitkIcon } from 'uitk-react-icons';
import { UitkLink } from 'uitk-react-link';

import { ActionDetailsType } from '__generated__/api';
import { DoubleLink } from 'components/common/media/double-link';
import { ListingComponentProps } from 'components/search/listing';
import { MessagingCardActions } from 'components/common/messaging-card/messaging-card-actions';
import { TargetableLink } from 'components/common/targetable-link';

const DoubleLinkTracked = ClickTracker(DoubleLink);

export interface FooterProps extends ListingComponentProps {
  listingClickHandler?: () => void;
  listingFooter?: ListingFooterViewModel;
}

export const ListingFooter: React.FunctionComponent<FooterProps> = ({
  analytics,
  listingFooter,
  routeStore,
  context,
  model,
  dataAttributes,
  listingClickHandler,
  referralObject,
  uiPage,
}) => {
  if (!model || !listingFooter) {
    return null;
  }

  const { hasAvailableOffer, propertyDetailsLink, name, travelAd } = model;
  const { actionModel, icon, linkName } = listingFooter;

  if ((!hasAvailableOffer && actionModel?.actionDetails?.name !== ActionDetailsType.DATEPICKER) || !actionModel?.text) {
    return null;
  }

  const { href, rfrr, text, actionDetails } = actionModel;

  const footerClassNames = ['uitk-cell', 'uitk-card-separator-top', 'all-x-padding-three', 'all-y-padding-two'];
  const clickableFooterClassNames = [...footerClassNames, 'uitk-card-aloha-action-area'];

  const listingDataAttributes = dataAttributes ? dataAttributes.listing : {};
  const analyticHotelOpenProps = referralObject ? referralObject.navigation : {};

  const hotelLinkProps = {
    name,
    rfrr,
    'data-stid': 'listing-footer-open-infosite',
    linkName: 'View selected hotel',
    to: propertyDetailsLink?.uri?.relativePath,
    onClick: listingClickHandler,
    ...analyticHotelOpenProps,
    ...listingDataAttributes,
  };

  const signInLinkProps = {
    'data-stid': 'listing-footer-sign-in',
    linkName,
    onClick: MessagingCardActions.resolveAction(routeStore, actionModel, analytics),
    rfrr,
    to: href,
    trackImmediate: true,
  };

  const brandedDealLinkProps = {
    ...signInLinkProps,
    'data-stid': 'listing-footer-branded-deal',
    children: text,
    onClick: MessagingCardActions.resolveAction(routeStore, actionModel, analytics, null, href),
  };

  let doubleLinkProps;
  let buttonLinkProps;
  let shouldLinkTarget = false;
  let footerClasses = footerClassNames;

  const actionType = actionDetails && actionDetails.name;

  switch (actionType) {
    case 'OPEN':
      doubleLinkProps = hotelLinkProps;

      if (!(travelAd && travelAd.clickRedirectLinkoffTrackingUrl)) {
        shouldLinkTarget = true;
      }

      break;
    case 'AUTHWINDOW':
      doubleLinkProps = signInLinkProps;

      break;
    case 'DATEPICKER':
      buttonLinkProps = {
        onClick: () => uiPage.dispatchOpenNewDatePickerEvent(),
        analytics: analytics.setupTracker(rfrr, text),
        linkName: text,
        href,
      };

      break;
    case 'EXTERNAL_LINK':
      doubleLinkProps = brandedDealLinkProps;
      shouldLinkTarget = true;
      footerClasses = clickableFooterClassNames;

      break;
    default:
      return null;
  }

  const isMobile = context && context.deviceInformation && context.deviceInformation.mobile;

  const innerClasses = classNames(
    ['uitk-flex', 'uitk-flex-align-items-center', 'uitk-type-medium', 'pwa-theme--link', 'uitk-flex-row-reverse'],
    { 'uitk-flex-justify-content-space-between': isMobile }
  );

  return (
    <>
      <footer className={`${footerClasses.join(' ')}`}>
        {buttonLinkProps ? (
          <ButtonLink {...buttonLinkProps} />
        ) : (
          <div className={innerClasses}>
            <IconDoubleLink icon={icon} shouldLinkTarget={shouldLinkTarget} doubleLinkProps={doubleLinkProps} />
            <TextDoubleLink text={text} shouldLinkTarget={shouldLinkTarget} doubleLinkProps={doubleLinkProps} />
          </div>
        )}
      </footer>
    </>
  );
};

interface IconDoubleLinkModel {
  icon?: string;
  shouldLinkTarget?: boolean;
  doubleLinkProps?: Record<string, unknown>;
}
const IconDoubleLink = ({ icon, shouldLinkTarget, doubleLinkProps }: IconDoubleLinkModel) => {
  if (!icon) {
    return null;
  }

  if (shouldLinkTarget) {
    return (
      <TargetableLink>
        <DoubleLinkTracked {...doubleLinkProps}>
          <UitkIcon name={icon} />
        </DoubleLinkTracked>
      </TargetableLink>
    );
  }

  return (
    <DoubleLinkTracked {...doubleLinkProps}>
      <UitkIcon name={icon} />
    </DoubleLinkTracked>
  );
};

interface TextDoubleLinkModel {
  text?: string;
  shouldLinkTarget?: boolean;
  doubleLinkProps?: Record<string, unknown>;
}
const TextDoubleLink = ({ text, shouldLinkTarget, doubleLinkProps }: TextDoubleLinkModel) => {
  if (shouldLinkTarget) {
    return (
      <TargetableLink>
        <DoubleLinkTracked {...doubleLinkProps}>{text}</DoubleLinkTracked>
      </TargetableLink>
    );
  }

  return <DoubleLinkTracked {...doubleLinkProps}>{text}</DoubleLinkTracked>;
};

interface ButtonLinkProps {
  onClick: () => void;
  linkName: string;
  href: string;
  analytics?: AnalyticsConfig;
}
const ButtonLink = ({ onClick, analytics, linkName, href }: ButtonLinkProps) => {
  const [isMounted, setIsMounted] = React.useState(false);
  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <UitkLink onClick={onClick} analytics={analytics}>
      {isMounted ? <button>{linkName}</button> : <a href={href}>{linkName}</a>}
    </UitkLink>
  );
};
ButtonLink.displayName = 'ButtonLink';
